// Textarea with @-mention autocomplete. Typing @ opens a dropdown of active
// staff filtered by what follows @. Arrow keys navigate, Enter/Tab selects,
// Esc dismisses. On select, "@<Name> " is inserted and the cursor moves
// past the insertion.
//
// Mentions are plain text — the worker parses them out on POST /tasks/:id/updates.

import { useState, useRef, useMemo, useEffect } from 'react';
import { Avatar } from './Avatar.jsx';

// A char that may precede @ and still start a mention: start-of-input,
// whitespace, common CN/EN punctuation, OR a CJK character — the last one
// matters because Chinese is written without spaces, so "@" is almost always
// typed right after a Chinese char (e.g. "需要和@Demi"). Without CJK here the
// dropdown never opened mid-sentence and users thought @ was broken. Latin
// letters stay excluded so "email@host" doesn't false-trigger.
const MENTION_BOUNDARY = /[\s,.!?，。！？、:;()㐀-䶿一-鿿豈-﫿]/;

// Pure trigger detection: is the cursor inside an "@query"? Returns
// { start, filter } (start = index of @) or null. Exported for tests.
export function findMentionQuery(text, cursor) {
  let i = cursor - 1;
  while (i >= 0 && text[i] !== '@') {
    if (/\s/.test(text[i])) return null;     // whitespace between @ and cursor → not a mention
    i--;
  }
  if (i < 0 || text[i] !== '@') return null;
  const prevChar = i > 0 ? text[i - 1] : '';
  if (!(i === 0 || MENTION_BOUNDARY.test(prevChar))) return null;
  const filter = text.slice(i + 1, cursor);
  if (filter.length > 16) return null;
  return { start: i, filter };
}

export const MentionTextarea = ({
  value, onChange, staff = [], placeholder, className = '', onKeyDown,
  rows = 3, style, autoSize = false, ...rest
}) => {
  const ref = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);

  // When autoSize is on, the textarea grows to fit its content (no inner
  // scrollbar). Recompute after every value change so paste / undo / typing
  // all stay in sync. The min-height comes from `rows`; we let scrollHeight
  // expand past that.
  useEffect(() => {
    if (!autoSize || !ref.current) return;
    const ta = ref.current;
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
  }, [value, autoSize]);

  const candidates = useMemo(() => {
    const active = staff.filter(s => s.active && s.role !== 'system');
    if (!filter) return active.slice(0, 12);
    const q = filter.toLowerCase();
    return active.filter(s =>
      s.name.toLowerCase().includes(q) ||
      (s.title || '').toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [staff, filter]);

  // Keep selectedIdx within bounds
  useEffect(() => {
    if (selectedIdx >= candidates.length) setSelectedIdx(Math.max(0, candidates.length - 1));
  }, [candidates.length, selectedIdx]);

  function updateMenuState(newVal, cursor) {
    const q = findMentionQuery(newVal, cursor);
    if (q) {
      setMentionStart(q.start);
      setFilter(q.filter);
      setShowMenu(true);
      setSelectedIdx(0);
      return;
    }
    setShowMenu(false);
    setMentionStart(-1);
  }

  function handleChange(e) {
    const newVal = e.target.value;
    onChange(newVal);
    updateMenuState(newVal, e.target.selectionStart);
  }

  function selectStaff(s) {
    if (mentionStart < 0 || !ref.current) return;
    const before = value.slice(0, mentionStart);
    const cursor = ref.current.selectionStart;
    const after = value.slice(cursor);
    const insertion = `@${s.name} `;
    const newText = before + insertion + after;
    onChange(newText);
    setShowMenu(false);
    setMentionStart(-1);
    // Restore focus + move cursor after the insertion
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const pos = before.length + insertion.length;
      ref.current.focus();
      ref.current.setSelectionRange(pos, pos);
    });
  }

  function handleKeyDown(e) {
    if (showMenu && candidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(i => (i + 1) % candidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(i => (i - 1 + candidates.length) % candidates.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        selectStaff(candidates[selectedIdx]);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        selectStaff(candidates[selectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMenu(false);
        setMentionStart(-1);
        return;
      }
    }
    onKeyDown?.(e);
  }

  // Recompute menu state on selection change (cursor move without text change)
  function handleSelect(e) {
    updateMenuState(value, e.target.selectionStart);
  }

  return (
    <div className={`mention-wrap ${className}`} style={style}>
      <textarea ref={ref} value={value} rows={rows}
                placeholder={placeholder}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onSelect={handleSelect}
                onBlur={() => setTimeout(() => setShowMenu(false), 100)}
                className="vava-textarea"
                style={autoSize ? { overflow: 'hidden', resize: 'none' } : undefined}
                {...rest} />
      {showMenu && candidates.length > 0 && (
        <ul className="mention-dropdown" role="listbox">
          {candidates.map((s, i) => (
            <li key={s.id}
                role="option"
                aria-selected={i === selectedIdx}
                className={i === selectedIdx ? 'selected' : ''}
                onMouseEnter={() => setSelectedIdx(i)}
                onMouseDown={(e) => { e.preventDefault(); selectStaff(s); }}>
              <Avatar staff={s} />
              <div className="mention-meta">
                <div className="mention-name">{s.name}</div>
                <div className="mention-title">{s.title || s.id}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
